import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import styles from './button.module.css'

const noop = Function.prototype

/**
 * @readonly
 * @enum {string}
 */
const BUTTON_TYPES = {
    BUTTON: 'button',
    RESET: 'reset',
    SUBMIT: 'submit'
}

const propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    dataAttributes: PropTypes.object,
    destructive: PropTypes.bool,
    disabled: PropTypes.bool,
    icon: PropTypes.bool,
    noBorder: PropTypes.bool,
    mini: PropTypes.bool,
    onClick: PropTypes.func,
    primary: PropTypes.bool,
    type: PropTypes.oneOf(Object.values(BUTTON_TYPES))
}

const defaultProps = {
    type: BUTTON_TYPES.BUTTON
}

/**
 * Render a single button component.
 *
 * @example <caption>Simple usage</caption>
 * <code>
 * <Button>I am a button!</Button>
 * <Button primary>Submit</Button>
 * <Button mini>Mini button</Button>
 * <Button icon><CalendarIcon size="14"/></Button>
 * </code>
 *
 * @param {Object} [props.dataAttributes] - Key Value Pairs for Data Attributes
 * @param {boolean} [props.destructive] - Apply destructive styles
 * @param {boolean} [props.disabled] - Add disabled attribute and styles
 * @param {boolean} [props.icon] - Apply icon (only) styles
 * @param {boolean} [props.mini] - Apply mini styling for small buttons
 * @param {Function} [props.onClick] - Click callback
 * @param {boolean} [props.primary] - Apply primary styles
 * @param {string} [props.type='button'] - The button's type attribute
 * @returns {ReactElement}
 */
function Button(props) {
    const {
        children,
        className,
        dataAttributes,
        destructive,
        disabled,
        icon,
        mini,
        noBorder,
        onClick,
        primary,
        ...restProps
    } = props

    const _onClick = disabled ? noop : onClick
    const composedClassName = cx(
        styles['button'],
        {
            [styles['primary']]: primary,
            [styles['destructive']]: destructive,
            [styles['mini']]: mini,
            [styles['icon']]: icon,
            [styles['no-border']]: noBorder
        },
        className,
        't3sel-button'
    )

    return (
        <button {...restProps} {...dataAttributes} className={composedClassName} disabled={disabled} onClick={_onClick}>
            {(icon && <span className={styles['flex-center']}>{children}</span>) || children}
        </button>
    )
}

Button.propTypes = propTypes
Button.defaultProps = defaultProps

export { Button }
